(() => {
  if (customElements.get('pbh-form')) return;

  const STRINGS = () =>
    window.pbhFormStrings || {
      emailRequired: 'Please enter your email address.',
      nameRequired: 'Please enter your name.',
      messageRequired: 'Please enter a message.',
      invalidEmail: 'Please enter a valid email address.',
      fieldRequired: 'This field is required.',
      errorsSummary: 'Please correct the errors below.',
    };

  class PBHForm extends HTMLElement {
    static ensureTagsInput(form) {
      if (!form) return null;

      let tagsInput = form.querySelector('[data-pbh-tags-input], input[name="contact[tags]"]');
      if (tagsInput) return tagsInput;

      tagsInput = document.createElement('input');
      tagsInput.type = 'hidden';
      tagsInput.name = 'contact[tags]';
      tagsInput.dataset.pbhTagsInput = '';
      form.appendChild(tagsInput);
      return tagsInput;
    }

    static syncTagInput(form) {
      if (!form) return '';

      const tags = PBHForm.collectInterestTags(form);
      const tagsInput = form.querySelector('[data-pbh-tags-input], input[name="contact[tags]"]');
      if (tagsInput) tagsInput.value = tags;
      return tags;
    }

    static collectInterestTags(form) {
      if (!form) return '';

      const root = form.closest('.waitlist-form, .pbh-waitlist') || form;
      const checkedInputs = form.querySelectorAll(
        '[data-pbh-interest]:checked, input[type="checkbox"][data-tag]:checked'
      );
      const checkedChoices = root.querySelectorAll('[data-pbh-interest-choice][aria-checked="true"]');

      return [...checkedInputs, ...checkedChoices]
        .map((el) => el.dataset.tag || el.value)
        .filter(Boolean)
        .join(',');
    }

    connectedCallback() {
      if (this.dataset.initialised === 'true') return;

      this.form = this.querySelector('form');
      if (!this.form) return;

      this.dataset.initialised = 'true';
      this.liveRegion =
        this.querySelector('.pbh-form__live-region') || this.#createLiveRegion();
      const interestRoot = this.form.closest('.waitlist-form, .pbh-waitlist') || this.form;
      this.interestCheckboxes = interestRoot.querySelectorAll(
        '[data-pbh-interest], input[type="checkbox"][data-tag], [data-pbh-interest-choice]'
      );
      this.submitButton = this.form.querySelector('[data-pbh-submit], button[type="submit"]');
      this.defaultSubmitLabel = this.submitButton?.textContent?.trim() || '';
      this.loadingLabel =
        this.dataset.loadingText ||
        this.submitButton?.dataset.loadingText ||
        this.defaultSubmitLabel;

      this.#bindTagSync();
      this.#bindInterestChoices();
      this.form.addEventListener('submit', this.#onSubmit.bind(this));
      this.form.addEventListener('input', this.#onInput.bind(this));
      this.form.addEventListener('change', this.#onChange.bind(this));

      this.#focusServerError();
      this.#bindHistoryBack();
    }

    disconnectedCallback() {
      this.#unbindTagSync();
    }

    #tagSyncHandler = null;

    #bindInterestChoices() {
      const choices = document.querySelectorAll(
        '.waitlist-form [data-pbh-interest-choice], .pbh-waitlist [data-pbh-interest-choice]'
      );

      choices.forEach((choice) => {
        choice.addEventListener('click', (event) => {
          if (!(choice instanceof HTMLElement)) return;
          if (event.target instanceof HTMLInputElement) return;

          const isChecked = choice.getAttribute('aria-checked') === 'true';
          choice.setAttribute('aria-checked', isChecked ? 'false' : 'true');
          PBHForm.syncTagInput(this.form);
        });

        choice.addEventListener('keydown', (event) => {
          if (!(event instanceof KeyboardEvent)) return;
          if (event.key !== ' ' && event.key !== 'Enter') return;
          event.preventDefault();
          choice.click();
        });
      });
    }

    #bindTagSync() {
      if (this.interestCheckboxes.length === 0) return;

      this.#tagSyncHandler = () => PBHForm.syncTagInput(this.form);
      this.interestCheckboxes.forEach((checkbox) => {
        if (checkbox.matches('[data-pbh-interest-choice]')) return;
        checkbox.addEventListener('change', this.#tagSyncHandler);
      });
    }

    #unbindTagSync() {
      if (!this.#tagSyncHandler) return;

      this.interestCheckboxes.forEach((checkbox) => {
        checkbox.removeEventListener('change', this.#tagSyncHandler);
      });
      this.#tagSyncHandler = null;
    }

    #createLiveRegion() {
      const region = document.createElement('div');
      region.className = 'pbh-form__live-region visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      this.prepend(region);
      return region;
    }

    #onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-pbh-interest], [data-tag]')) return;
      PBHForm.syncTagInput(this.form);
    }

    #onInput(event) {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches('input, textarea, select')) return;
      this.#clearFieldError(target);
    }

    #onSubmit(event) {
      const errors = this.#validate();
      if (errors.length > 0) {
        event.preventDefault();
        this.#showErrors(errors);
        return;
      }

      if (this.form.hasAttribute('data-waitlist-form')) {
        event.preventDefault();
        this.#submitWaitlistForm();
        return;
      }

      PBHForm.ensureTagsInput(this.form);
      PBHForm.syncTagInput(this.form);
      this.#setLoading(true);
    }

    async #submitWaitlistForm() {
      this.#setLoading(true);

      const formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');

      const token =
        this.querySelector('[data-waitlist-csrf-bridge] input[name="authenticity_token"]')?.value ||
        this.form.dataset.authenticityToken;
      if (token) formData.append('authenticity_token', token);
      const recaptchaToken =
      this.querySelector('[data-waitlist-csrf-bridge] input[name="recaptcha-v3-token"]')?.value;
      if (recaptchaToken) formData.append('recaptcha-v3-token', recaptchaToken);

      const tags = PBHForm.collectInterestTags(this.form);
      if (tags) formData.append('contact[tags]', tags);

      this.form.querySelectorAll('input, textarea, select').forEach((field) => {
        if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
          return;
        }
        if (field.type === 'hidden' || field.type === 'submit' || !field.name) return;
        formData.append(field.name, field.value);
      });

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'text/html',
          },
        });

        if (response.ok) {
          this.#showWaitlistSuccess();
          return;
        }

        this.#setLoading(false);
        this.liveRegion.textContent = STRINGS().errorsSummary;
      } catch {
        this.#setLoading(false);
        this.liveRegion.textContent = STRINGS().errorsSummary;
      }
    }

    #showWaitlistSuccess() {
      this.#setLoading(false);

      const waitlistRoot = this.querySelector('.waitlist-form');
      waitlistRoot?.querySelector('[data-waitlist-fine-print]')?.classList.add('hidden');

      const heading = this.dataset.successMessage || '';
      const followup = this.dataset.successFollowup || '';

      const success = document.createElement('div');
      success.className = 'pbh-form__success waitlist-form__success';
      success.tabIndex = -1;
      success.innerHTML = `
        <h2 class="waitlist-form__success-heading h2">${heading}</h2>
        <p class="waitlist-form__success-text">${followup}</p>
      `;

      this.form.replaceWith(success);
      success.focus();
    }

    #validate() {
      const strings = STRINGS();
      const errors = [];
      const fields = this.form.querySelectorAll('input, textarea, select');

      fields.forEach((field) => {
        if (!(field instanceof HTMLElement)) return;
        if (field instanceof HTMLInputElement && field.type === 'hidden') return;
        if (field.disabled) return;
        if (field instanceof HTMLInputElement && field.type === 'checkbox' && !field.required) {
          return;
        }

        const isRequired =
          field.hasAttribute('required') || field.getAttribute('aria-required') === 'true';
        const value = 'value' in field ? field.value.trim() : '';

        if (isRequired && !value) {
          errors.push({ field, message: this.#messageForField(field, strings) });
          return;
        }

        if (field instanceof HTMLInputElement && field.type === 'email' && value && !field.checkValidity()) {
          errors.push({ field, message: strings.invalidEmail });
        }
      });

      return errors;
    }

    #messageForField(field, strings) {
      if (field.dataset.pbhErrorMessage) return field.dataset.pbhErrorMessage;

      if (field instanceof HTMLInputElement && field.type === 'email') return strings.emailRequired;

      const key = field.dataset.pbhErrorKey;
      if (key && strings[`${key}Required`]) return strings[`${key}Required`];

      const name = field.getAttribute('name') || '';
      if (/email/i.test(name)) return strings.emailRequired;
      if (/name/i.test(name)) return strings.nameRequired;
      if (/message|body|comment/i.test(name)) return strings.messageRequired;

      return strings.fieldRequired;
    }

    #showErrors(errors) {
      this.#clearClientErrors();
      this.classList.remove('pbh-form--loading');
      this.#setLoading(false);

      errors.forEach(({ field, message }) => {
        this.#setFieldError(field, message);
      });

      const summary = errors.map(({ message }) => message).join(' ');
      this.liveRegion.textContent = `${STRINGS().errorsSummary} ${summary}`;

      const firstField = errors[0]?.field;
      if (firstField instanceof HTMLElement) {
        firstField.focus();
      }
    }

    #setFieldError(field, message) {
      const wrapper = field.closest('.field');
      if (wrapper) wrapper.classList.add('field--with-error');

      field.setAttribute('aria-invalid', 'true');

      const errorId = `${field.id || field.name}-error`.replace(/[\[\]]/g, '-');
      let errorEl = this.form.querySelector(`[id="${errorId}"]`);

      if (!errorEl) {
        errorEl = document.createElement('small');
        errorEl.id = errorId;
        errorEl.className = 'pbh-form__field-error contact__field-error';

        const hiddenLabel = document.createElement('span');
        hiddenLabel.className = 'visually-hidden';
        hiddenLabel.textContent = STRINGS().errorsSummary;

        const messageEl = document.createElement('span');
        messageEl.className = 'form__message';
        messageEl.textContent = message;

        errorEl.append(hiddenLabel, messageEl);
        wrapper?.append(errorEl);
      } else {
        const messageEl = errorEl.querySelector('.form__message') || errorEl;
        messageEl.textContent = message;
        errorEl.hidden = false;
        errorEl.classList.remove('hidden');
      }

      const describedBy = new Set(
        (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
      );
      describedBy.add(errorId);
      field.setAttribute('aria-describedby', [...describedBy].join(' '));
    }

    #clearFieldError(field) {
      if (!(field instanceof HTMLElement)) return;

      const wrapper = field.closest('.field');
      wrapper?.classList.remove('field--with-error');
      field.removeAttribute('aria-invalid');

      const errorId = `${field.id || field.name}-error`.replace(/[\[\]]/g, '-');
      const errorEl = this.form.querySelector(`[id="${errorId}"]`);
      if (errorEl?.classList.contains('pbh-form__field-error')) {
        errorEl.remove();
      }

      const describedBy = (field.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== errorId);
      if (describedBy.length) {
        field.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        field.removeAttribute('aria-describedby');
      }
    }

    #clearClientErrors() {
      this.form.querySelectorAll('.pbh-form__field-error').forEach((errorEl) => errorEl.remove());
      this.form.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
        if (!(field instanceof HTMLElement)) return;
        if (field.closest('.field')?.querySelector('.contact__field-error, .form__message:not(.pbh-form__field-error .form__message)')) {
          return;
        }
        this.#clearFieldError(field);
      });
      this.form.querySelectorAll('.field--with-error').forEach((wrapper) => {
        if (wrapper.querySelector('.contact__field-error')) return;
        wrapper.classList.remove('field--with-error');
      });
    }

    #setLoading(isLoading) {
      if (!this.submitButton) return;

      this.classList.toggle('pbh-form--loading', isLoading);
      this.form.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.submitButton.disabled = isLoading;

      if (isLoading) {
        this.submitButton.textContent = this.loadingLabel;
      } else if (this.defaultSubmitLabel) {
        this.submitButton.textContent = this.defaultSubmitLabel;
      }
    }

    #focusServerError() {
      const serverErrorField = this.form.querySelector('[aria-invalid="true"]');
      if (serverErrorField instanceof HTMLElement) {
        serverErrorField.focus();
      }
    }

    #bindHistoryBack() {
      const backButton = this.querySelector('[data-pbh-history-back]');
      if (!(backButton instanceof HTMLButtonElement)) return;

      backButton.addEventListener('click', () => {
        const fallbackUrl = backButton.dataset.pbhFallbackUrl || '/';
        const referrer = document.referrer;
        let useHistory = false;

        if (referrer && window.history.length > 1) {
          try {
            const refUrl = new URL(referrer);
            useHistory =
              refUrl.origin === window.location.origin &&
              refUrl.pathname !== window.location.pathname;
          } catch {
            useHistory = false;
          }
        }

        if (useHistory) {
          window.history.back();
        } else {
          window.location.assign(fallbackUrl);
        }
      });
    }
  }

  customElements.define('pbh-form', PBHForm);
  window.PBHForm = PBHForm;
})();
